/**
 * Toggles body classes based on scroll direction.
 */
export const watchScroll = () => {
    let lastScrollTop = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
        const currentScroll = window.scrollY;

        if (!ticking) {
            requestAnimationFrame(() => {
                const isScrollingDown = currentScroll > lastScrollTop;

                if (currentScroll > 500) {
                    document.body.classList.toggle('scrolling-down', isScrollingDown);
                    document.body.classList.toggle('scrolling-up', !isScrollingDown);
                } else {
                    document.body.classList.remove('scrolling-down');
                    document.body.classList.remove('scrolling-up');
                }

                lastScrollTop = currentScroll;
                ticking = false;
            });
            ticking = true;
        }
    }, {passive: true});
};

/**
 * Adds helper classes based on the next component theme.
 */
export const hasLightNext = () => {
    document.querySelectorAll('.component.padding-regular').forEach(el => {
        if (el.nextElementSibling?.matches('.component.dark')) {
            el.classList.add('has-dark-next');
        } else {
            el.classList.add('has-light-next');
        }
    });
}

/**
 * Toggles team slider bios via delegated clicks.
 */
export const teamSliderBioToggle = () => {
    document.addEventListener('click', (event) => {
        const toggleButton = event.target.closest('.team-slider__bio-toggle');

        if (toggleButton) {
            const teamEntry = toggleButton.closest('.team-slider__entry');

            if (teamEntry) {
                teamEntry.classList.toggle('bio-visible');
            }
        }
    });
}

/**
 * Highlights the most visible post card on smaller screens.
 */
export const activePostsGrid = () => {
    const postsGrid = document.getElementById('posts-grid');

    if (!postsGrid) return;

    let observer;

    function trackVisiblePostCards() {
        if (window.innerWidth >= 991) {
            removeAllActiveClasses();
            return;
        }

        const postCards = postsGrid.querySelectorAll('.post-card');
        let topVisibleCard = null;
        let topVisiblePosition = Infinity;

        postCards.forEach(card => {
            const rect = card.getBoundingClientRect();
            const visible = rect.top < window.innerHeight && rect.bottom > 0;

            if (visible && rect.top < topVisiblePosition && rect.top > -rect.height/2) {
                topVisiblePosition = rect.top;
                topVisibleCard = card;
            }
        });

        removeAllActiveClasses();

        if (topVisibleCard) {
            const basicInfo = topVisibleCard.querySelector('.post-card__basic-info');
            if (basicInfo) {
                basicInfo.classList.add('active');
            }
        }
    }

    function removeAllActiveClasses() {
        postsGrid.querySelectorAll('.post-card__basic-info.active').forEach(basicInfo => {
            basicInfo.classList.remove('active');
        });
    }

    function initializeObserver() {
        if (observer) {
            observer.disconnect();
        }

        observer = new MutationObserver(() => {
            trackVisiblePostCards();
        });

        observer.observe(postsGrid, {
            childList: true,
            subtree: true
        });
    }

    initializeObserver();

    window.addEventListener('scroll', () => {
        requestAnimationFrame(trackVisiblePostCards);
    });

    window.addEventListener('resize', () => {
        requestAnimationFrame(trackVisiblePostCards);
    });

    trackVisiblePostCards();
}

/**
 * Toggles case study content panels.
 */
export const togglePostContent = () => {
    const toggleButtons = document.querySelectorAll('.case-studies-main-content-toggle');

    if (!toggleButtons.length) {
        console.warn('No toggle buttons found');
        return;
    }

    toggleButtons.forEach(toggleButton => {
        const contentWrapper = document.querySelector(`#${toggleButton.getAttribute('aria-controls')}`);

        if (!contentWrapper) {
            console.warn(`Content wrapper for button with ID ${toggleButton.id} not found`);
            return;
        }

        const handleToggle = () => {
            const isCurrentlyExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
            toggleButton.setAttribute('aria-expanded', (!isCurrentlyExpanded).toString());

            if (!contentWrapper.classList.contains('is-active')) {
                contentWrapper.style.height = '0px';
                contentWrapper.classList.add('is-active');
                const height = contentWrapper.scrollHeight;
                contentWrapper.style.height = height + 'px';
            } else {
                contentWrapper.style.height = '0px';
                contentWrapper.addEventListener('transitionend', () => {
                    contentWrapper.classList.remove('is-active');
                }, { once: true });
            }
        };

        toggleButton.addEventListener('click', handleToggle);

        const startExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
        if (startExpanded) {
            contentWrapper.classList.add('is-active');
            contentWrapper.style.height = contentWrapper.scrollHeight + 'px';
        }
    });
};

/**
 * Copies the current URL to the clipboard.
 */
export const copyLink = () => {
    document.querySelectorAll('.copy-link-button').forEach(button => {
        button.addEventListener('click', async (e) => {
            e.preventDefault();

            try {
                await navigator.clipboard.writeText(window.location.href);

                const originalText = button.textContent;
                button.textContent = 'Copied!';

                setTimeout(() => {
                    button.textContent = originalText;
                }, 2000);
            } catch (err) {
                console.error('Failed to copy URL:', err);
                const textarea = document.createElement('textarea');
                textarea.value = window.location.href;
                textarea.style.position = 'fixed';
                document.body.appendChild(textarea);
                textarea.select();

                try {
                    document.execCommand('copy');
                    button.textContent = 'Copied!';
                    setTimeout(() => {
                        button.textContent = originalText;
                    }, 2000);
                } catch (err) {
                    console.error('Fallback copy failed:', err);
                } finally {
                    document.body.removeChild(textarea);
                }
            }
        });
    });
}

/**
 * Handles open/close behavior for the mobile nav.
 */
export const mobileNavToggle = () => {
    const toggleButton = document.querySelector('.mobile-nav-toggle');
    const panel = document.getElementById('mobile-nav-panel');
    const overlay = document.querySelector('.mobile-nav__overlay');
    const menu = document.getElementById('menu-main-menu-mobile');

    if (!toggleButton || !panel || !menu) return;

    const closeMenu = () => {
        toggleButton.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('mobile-menu-open');
    };

    const openMenu = () => {
        toggleButton.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('mobile-menu-open');
    };

    toggleButton.addEventListener('click', () => {
        const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
            closeMenu();
            return;
        }
        openMenu();
    });

    if (overlay) {
        overlay.addEventListener('click', closeMenu);
    }

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    });

    menu.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link || !menu.contains(link)) return;

        const item = link.closest('.menu-item');
        if (!item) return;

        const isTopLevel = item.parentElement === menu;
        const hasChildren = item.classList.contains('menu-item-has-children');

        if (isTopLevel && hasChildren) {
            return;
        }

        closeMenu();
    });
};

/**
 * Initializes desktop nav dropdown interactions.
 */
export const initMainNavDropdowns = () => {
    const menu = document.getElementById('menu-main-menu');

    if (!menu) return;

    const items = Array.from(menu.querySelectorAll('.menu-item-has-children'));
    const indicator = document.createElement('span');
    let activeItem = null;
    let moveTimer = null;
    let hideTimer = null;
    const indicatorDelayMs = 120;

    indicator.className = 'menu-indicator';
    menu.appendChild(indicator);

    const moveIndicator = (item) => {
        const menuRect = menu.getBoundingClientRect();
        if (!menuRect.width) return;
        const itemRect = item.getBoundingClientRect();
        const submenu = item.querySelector(':scope > .sub-menu');
        if (!submenu) return;
        const submenuRect = submenu.getBoundingClientRect();
        const indicatorRect = indicator.getBoundingClientRect();
        const center = itemRect.left + (itemRect.width / 2) - menuRect.left - (indicatorRect.width / 2);
        const top = submenuRect.top - menuRect.top - indicatorRect.height + 1;
        indicator.style.setProperty('--indicator-x', `${center}px`);
        indicator.style.setProperty('--indicator-top', `${top}px`);
        indicator.classList.add('is-active');
        activeItem = item;
    };

    const scheduleIndicator = (item) => {
        if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
        if (moveTimer) {
            clearTimeout(moveTimer);
        }
        moveTimer = setTimeout(() => {
            moveIndicator(item);
            moveTimer = null;
        }, indicatorDelayMs);
    };

    const hideIndicator = () => {
        if (moveTimer) {
            clearTimeout(moveTimer);
            moveTimer = null;
        }
        indicator.classList.remove('is-active');
        activeItem = null;
    };

    const scheduleHideIndicator = () => {
        if (moveTimer) {
            clearTimeout(moveTimer);
            moveTimer = null;
        }
        if (hideTimer) {
            clearTimeout(hideTimer);
        }
        hideTimer = setTimeout(() => {
            hideIndicator();
            hideTimer = null;
        }, indicatorDelayMs);
    };

    const setItemState = (item, isOpen) => {
        const toggle = item.querySelector('.menu-toggle-button');
        const submenu = item.querySelector('.sub-menu');

        if (!toggle || !submenu) return;

        item.classList.toggle('is-open', isOpen);
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        submenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

        if (isOpen) {
            scheduleIndicator(item);
        } else if (activeItem === item) {
            scheduleHideIndicator();
        }
    };

    const closeAll = (exceptItem = null) => {
        items.forEach((item) => {
            if (item === exceptItem) return;
            setItemState(item, false);
        });
        if (!exceptItem) {
            scheduleHideIndicator();
        }
    };

    items.forEach((item, index) => {
        const toggle = item.querySelector('.menu-toggle-button');
        const submenu = item.querySelector('.sub-menu');

        if (!toggle || !submenu) return;

        const baseId = item.getAttribute('id') || `menu-item-${index}`;
        const submenuId = submenu.getAttribute('id') || `submenu-${baseId.replace('menu-item-', '')}`;

        if (!submenu.getAttribute('id')) {
            submenu.setAttribute('id', submenuId);
        }

        toggle.setAttribute('aria-controls', submenuId);
        toggle.setAttribute('aria-haspopup', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');

        if (!toggle.getAttribute('aria-label')) {
            const label = item.querySelector('> a')?.textContent?.trim();
            if (label) {
                toggle.setAttribute('aria-label', `Toggle submenu for ${label}`);
            }
        }

        toggle.addEventListener('click', (event) => {
            event.preventDefault();
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            closeAll(item);
            setItemState(item, !isOpen);
        });

        toggle.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggle.click();
            }
        });

        item.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                setItemState(item, false);
                toggle.focus();
            }
        });

        item.addEventListener('mouseenter', () => {
            closeAll(item);
            setItemState(item, true);
        });

        item.addEventListener('mouseleave', () => {
            if (!item.matches(':focus-within')) {
                setItemState(item, false);
            }
        });

        item.addEventListener('focusout', (event) => {
            if (!item.contains(event.relatedTarget)) {
                setItemState(item, false);
            }
        });
    });

    document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) {
            closeAll();
        }
    });

    menu.addEventListener('mouseleave', () => {
        hideIndicator();
        closeAll();
    });

    window.addEventListener('resize', () => {
        if (activeItem) {
            moveIndicator(activeItem);
        }
    });
};

/**
 * Initializes mobile nav dropdown interactions.
 */
export const initMobileNavDropdowns = () => {
    const menu = document.getElementById('menu-main-menu-mobile');

    if (!menu) return;

    const items = Array.from(menu.querySelectorAll('.menu-item-has-children'));

    const setItemState = (item, isOpen) => {
        const toggle = item.querySelector('.menu-toggle-button');
        const submenu = item.querySelector('.sub-menu');

        if (!toggle || !submenu) return;

        item.classList.toggle('is-open', isOpen);
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        submenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    };

    const closeAll = (exceptItem = null) => {
        items.forEach((item) => {
            if (item === exceptItem) return;
            setItemState(item, false);
        });
    };

    items.forEach((item, index) => {
        const toggle = item.querySelector('.menu-toggle-button');
        const submenu = item.querySelector('.sub-menu');
        const topLevelLink = item.querySelector(':scope > a');

        if (!toggle || !submenu) return;

        const baseId = item.getAttribute('id') || `menu-item-${index}`;
        const submenuId = submenu.getAttribute('id') || `submenu-${baseId.replace('menu-item-', '')}`;

        if (!submenu.getAttribute('id')) {
            submenu.setAttribute('id', submenuId);
        }

        toggle.setAttribute('aria-controls', submenuId);
        toggle.setAttribute('aria-haspopup', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');

        if (!toggle.getAttribute('aria-label')) {
            const label = item.querySelector('> a')?.textContent?.trim();
            if (label) {
                toggle.setAttribute('aria-label', `Toggle submenu for ${label}`);
            }
        }

        toggle.addEventListener('click', (event) => {
            event.preventDefault();
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            closeAll(item);
            setItemState(item, !isOpen);
        });

        if (topLevelLink) {
            topLevelLink.addEventListener('click', (event) => {
                event.preventDefault();
                const isOpen = item.classList.contains('is-open');
                closeAll(item);
                setItemState(item, !isOpen);
            });
        }
    });
};

/**
 * Adds in-viewport classes to large-screen components.
 */
export const monitorSectionsInViewport = () => {
    const sections = document.querySelectorAll('.component');

    if (!sections.length) {
        console.warn('No .component elements found to monitor');
        return;
    }

    if (window.innerWidth < 992) {
        sections.forEach((section) => section.classList.add('in-viewport'));
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-viewport');
                    observer.unobserve(entry.target);
                }
            });
        },
        {
            rootMargin: '-20% 0px -20% 0px',
            threshold: 0
        }
    );

    sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const isPastTriggerPoint = rect.top < viewportHeight * 0.75;

        if (isPastTriggerPoint) {
            section.classList.add('in-viewport');
        }

        observer.observe(section);
    });
};

/**
 * Syncs active state on main nav links.
 */
export const updateActiveNav = () => {
    document.body.addEventListener("click", function (event) {
        const clickedLink = event.target.closest("#menu-main-menu .menu-item a");

        if (clickedLink) {
            setActiveNav(clickedLink);
        }
    });

    setActiveNav(document.querySelector("#menu-main-menu .menu-item a[href='" + window.location.pathname + "']"));
};

const setActiveNav = (clickedLink) => {
    if (!clickedLink) return;

    const menuItems = document.querySelectorAll("#menu-main-menu .menu-item");

    menuItems.forEach(item => {
        item.classList.remove("current-menu-item", "current_page_item");
    });

    clickedLink.closest(".menu-item").classList.add("current-menu-item", "current_page_item");
}

/**
 * Sets a cookie for the current domain.
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
export const setCookie = function(name, value, days) {
    var expires = "";
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
}
